import { useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';

interface Driver {
  id: string | number;
  first_name?: string;
  last_name?: string;
}

interface ClockStatusResponse {
  clock_in?: string;
  clocked_in?: boolean;
}

interface AttendanceResponse {
  error?: string;
  stop_bonus_eligible?: boolean;
}

interface ExistingEntry {
  id?: string | number;
  present?: boolean;
  absence_reason?: string;
  notes?: string;
  terminal_name?: string;
  terminal_number?: string;
  route_number?: string;
  route_id?: string;
  deliveries?: number | null;
  pickups?: number | null;
  miles?: number | null;
  truck_number?: string;
  status?: string;
}

interface WorkedForm {
  terminalName: string;
  routeNumber: string;
  deliveries: string;
  pickups: string;
  miles: string;
  truckNumber: string;
}

export function useDailyEntryActions(selectedDate: string, refreshRoster: () => Promise<void>) {
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [clockStatus, setClockStatus] = useState<ClockStatusResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [absenceReason, setAbsenceReason] = useState('');
  const [editingEntryId, setEditingEntryId] = useState<string | number | null>(null);
  const [absenceNotes, setAbsenceNotes] = useState('');
  const [workedForm, setWorkedForm] = useState({
    terminalName: '',
    routeNumber: '',
    deliveries: '',
    pickups: '',
    miles: '',
    truckNumber: '',
  });

  async function openWorked(driver: Driver) {
    setSelectedDriver(driver);
    setActiveAction('worked');
    setActionError(null);
    setSuccessMessage(null);
    setWorkedForm({
      terminalName: '',
      routeNumber: '',
      deliveries: '',
      pickups: '',
      miles: '',
      truckNumber: '',
    });

    try {
      const data = await apiFetch<ClockStatusResponse>(`/api/clock/status/${driver.id}/${selectedDate}`);
      setClockStatus(data);
    } catch (err) {
      setClockStatus(null);
    }
  }

  async function openAbsent(driver: Driver) {
    setSelectedDriver(driver);
    setActiveAction('absent');
    setActionError(null);
    setSuccessMessage(null);
    setAbsenceReason('');
    setAbsenceNotes('');

    try {
      const data = await apiFetch<ClockStatusResponse>(`/api/clock/status/${driver.id}/${selectedDate}`);
      setClockStatus(data);
    } catch (err) {
      setClockStatus(null);
    }
  }

  async function submitWorked() {
    if (!selectedDriver) return false;
    setActionError(null);

    if (!workedForm.terminalName.trim()) {
      setActionError('Terminal is required.');
      return false;
    }
    if (!workedForm.routeNumber.trim()) {
      setActionError('Route is required.');
      return false;
    }
    if (!workedForm.deliveries) {
      setActionError('Deliveries is required.');
      return false;
    }
    if (!workedForm.pickups) {
      setActionError('Pickups is required.');
      return false;
    }
    if (!workedForm.miles) {
      setActionError('Miles is required.');
      return false;
    }

    setSubmitting(true);
    try {
      const response = await apiFetch<AttendanceResponse>('/api/attendance', {
        method: 'POST',
        body: JSON.stringify({
          employee_id: selectedDriver.id,
          date: selectedDate,
          present: true,
          entry_type: 'driver',
          terminal_name: workedForm.terminalName.trim(),
          route_number: workedForm.routeNumber.trim(),
          deliveries: parseInt(workedForm.deliveries, 10),
          pickups: parseInt(workedForm.pickups, 10),
          miles: parseInt(workedForm.miles, 10),
          truck_number: workedForm.truckNumber.trim() || null,
          route_type: 'local',
        }),
      });

      if (response.error) {
        setActionError(response.error);
        return false;
      }

      const totalStops = parseInt(workedForm.deliveries, 10) + parseInt(workedForm.pickups, 10);
      const bonusText = response.stop_bonus_eligible ? 'Yes' : 'No';
      setSuccessMessage(`Driver entry saved for ${selectedDriver.first_name} ${selectedDriver.last_name}. Stops: ${totalStops}, Bonus: ${bonusText}.`);
      setActiveAction(null);
      setSelectedDriver(null);
      setClockStatus(null);
      setWorkedForm({ terminalName: '', routeNumber: '', deliveries: '', pickups: '', miles: '', truckNumber: '' });
      await refreshRoster();
      return true;
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to submit worked entry.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function submitAbsent() {
    if (!selectedDriver) return;
    setActionError(null);

    if (!absenceReason) {
      setActionError('Select an absence reason.');
      return false;
    }

    if (absenceReason === 'other' && !absenceNotes.trim()) {
      setActionError('Notes required for Other reason.');
      return false;
    }

    setSubmitting(true);
    try {
      const response = await apiFetch<AttendanceResponse>('/api/attendance', {
        method: 'POST',
        body: JSON.stringify({
          employee_id: selectedDriver.id,
          date: selectedDate,
          present: false,
          absence_reason: absenceReason,
          notes: absenceNotes.trim() || null,
        }),
      });

      if (response.error) {
        setActionError(response.error);
        return false;
      }

      const reasonDisplay = absenceReason.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
      setSuccessMessage(`Absence recorded for ${selectedDriver.first_name} ${selectedDriver.last_name}. Reason: ${reasonDisplay}.`);
      setActiveAction(null);
      setSelectedDriver(null);
      setClockStatus(null);
      setAbsenceReason('');
      setAbsenceNotes('');
      await refreshRoster();
      return true;
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to submit absence.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function openEdit(driver: Driver, existingEntry: ExistingEntry) {
    setSelectedDriver(driver);
    setActiveAction('edit');
    setActionError(null);
    setSuccessMessage(null);
    setEditingEntryId(existingEntry?.id || null);

    if (existingEntry?.present === false) {
      // Editing an absence
      setAbsenceReason(existingEntry.absence_reason || '');
      setAbsenceNotes(existingEntry.notes || '');
      setWorkedForm({ terminalName: '', routeNumber: '', deliveries: '', pickups: '', miles: '', truckNumber: '' });
    } else {
      // Editing a worked entry
      setWorkedForm({
        terminalName: existingEntry?.terminal_name || existingEntry?.terminal_number || '',
        routeNumber: existingEntry?.route_number || existingEntry?.route_id || '',
        deliveries: existingEntry?.deliveries != null ? String(existingEntry.deliveries) : '',
        pickups: existingEntry?.pickups != null ? String(existingEntry.pickups) : '',
        miles: existingEntry?.miles != null ? String(existingEntry.miles) : '',
        truckNumber: existingEntry?.truck_number || '',
      });
      setAbsenceReason('');
      setAbsenceNotes('');
    }

    try {
      const data = await apiFetch<ClockStatusResponse>(`/api/clock/status/${driver.id}/${selectedDate}`);
      setClockStatus(data);
    } catch (err) {
      setClockStatus(null);
    }
  }

  async function submitEdit() {
    if (!selectedDriver || !editingEntryId) return false;
    setActionError(null);
    setSubmitting(true);

    try {
      const updates: Record<string, string | number | null> = {};
      // Send all editable fields — server picks what changed
      if (workedForm.deliveries) updates.deliveries = parseInt(workedForm.deliveries, 10);
      if (workedForm.pickups) updates.pickups = parseInt(workedForm.pickups, 10);
      if (workedForm.miles) updates.miles = parseInt(workedForm.miles, 10);
      if (workedForm.truckNumber !== undefined) updates.truck_number = workedForm.truckNumber.trim() || null;
      if (workedForm.routeNumber) updates.route_number = workedForm.routeNumber.trim();
      if (workedForm.terminalName) updates.terminal_name = workedForm.terminalName.trim();
      if (absenceReason) updates.absence_reason = absenceReason;
      if (absenceNotes) updates.notes = absenceNotes.trim() || null;

      const response = await apiFetch<AttendanceResponse>(`/api/attendance/${editingEntryId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });

      if (response.error) {
        setActionError(response.error);
        return false;
      }

      setSuccessMessage(`Entry updated for ${selectedDriver.first_name} ${selectedDriver.last_name}.`);
      setActiveAction(null);
      setSelectedDriver(null);
      setEditingEntryId(null);
      setClockStatus(null);
      setWorkedForm({ terminalName: '', routeNumber: '', deliveries: '', pickups: '', miles: '', truckNumber: '' });
      setAbsenceReason('');
      setAbsenceNotes('');
      await refreshRoster();
      return true;
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to update entry.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  function closeAction() {
    setSelectedDriver(null);
    setActiveAction(null);
    setClockStatus(null);
    setActionError(null);
    setEditingEntryId(null);
    setAbsenceReason('');
    setAbsenceNotes('');
    setWorkedForm({ terminalName: '', routeNumber: '', deliveries: '', pickups: '', miles: '', truckNumber: '' });
  }

  return {
    selectedDriver,
    activeAction,
    clockStatus,
    submitting,
    actionError,
    successMessage,
    absenceReason,
    absenceNotes,
    workedForm,
    editingEntryId,
    setWorkedForm,
    setAbsenceReason,
    setAbsenceNotes,
    openWorked,
    openAbsent,
    openEdit,
    submitWorked,
    submitAbsent,
    submitEdit,
    closeAction,
  };
}
